import { isEmpty, isNil } from 'lodash';

import isPromise from './is-promise';
import { getPreparedPreloadImage } from './image';

import type { HelperInstagramImageSize, Post, PostToDelete } from 'types';

type PostsAvailabilityMap = {
  availablePosts: Array<Post>,
  unavailablePosts: Array<Post>
};

export const getPostsToDelete = (posts: Array<PostToDelete> = []): Array<string> => {
  if (isNil(posts)) {
    return [];
  }

  const postsNotFound = posts.filter(post => isEmpty(post.assetUrl) || isNil(post.assetUrl));

  return postsNotFound.map(post => post.code);
};

export const getPostCodeFromUrl = (url: string | null): string => {
  if (isNil(url)) {
    return '';
  }

  const regex = /([a-z0-9-_]){11}$/i;
  const match = url.match(regex);

  return match?.[0] || '';
};

export const getTikTokVideoCodeFromUrl = (url: string | null): string => {
  if (isNil(url)) {
    return '';
  }

  const regex = /\/video\/([0-9]+)/i;
  const match = url.match(regex);

  return match?.[1] || '';
};

function mergePostsMap(oldAcc, newAcc) {
  return {
    availablePosts: oldAcc.availablePosts.concat(newAcc.availablePosts),
    unavailablePosts: oldAcc.unavailablePosts.concat(newAcc.unavailablePosts)
  };
}

function prepPostsMap(posts: Array<Post> = []): PostsAvailabilityMap {
  return {
    availablePosts: posts.filter(post => post.assetUrl),
    unavailablePosts: posts.filter(post => !post.assetUrl)
  };
}

function fetchPosts<T extends { url?: string }>(
  allPosts: Array<T> = [],
  getPostByUrl,
  size: HelperInstagramImageSize = 'l'
) {
  const promises = allPosts.map(async (post) => {
    const imageUrlInitial = getPostByUrl(post.url, size, post);
    let imageUrl = '';

    if (isPromise(imageUrlInitial)) {
      imageUrl = await imageUrlInitial;
    } else if (typeof imageUrlInitial === 'string') {
      imageUrl = imageUrlInitial;
    }

    return getPreparedPreloadImage(post, imageUrl);
  });

  return Promise.all(promises);
}

export function getPostsByAvailability(
  posts: Array<Post> = [],
  count: number,
  getPostByUrl: (c: string, b: HelperInstagramImageSize, a: Post) => string | Promise<string>,
  acc: PostsAvailabilityMap = prepPostsMap(),
  size: HelperInstagramImageSize = 'l',
  startIndex: number = 0
): Promise<PostsAvailabilityMap> {
  return fetchPosts(posts.slice(startIndex, startIndex + count), getPostByUrl, size)
    .then(fetchedPosts => prepPostsMap(fetchedPosts))
    .then(postsMap => {
      const { availablePosts } = postsMap;

      const newAcc = mergePostsMap(acc, postsMap);
      const remainingPosts = posts.slice(startIndex + count);
      if (availablePosts.length < count && remainingPosts.length) {
        return getPostsByAvailability(remainingPosts, count - availablePosts.length, getPostByUrl, newAcc);
      }
      return newAcc;
    });
}
